"use client";

import { useState, useEffect } from "react";
import { Maximize2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import ResponsiveImage from "@/components/responsive-image";
import { galleryItems, type GalleryCategory } from "@/content/gallery";

export function PlasteringGallery() {
  const [activeFilter, setActiveFilter] = useState<"all" | GalleryCategory>(
    "all",
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const sectionRef = useRevealOnScroll<HTMLElement>({
    threshold: 0.1,
    staggerInterval: 50,
  });

  const filteredItems =
    activeFilter === "all"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeFilter);

  useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxOpen]);

  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredItems.length);
  };

  const prevImage = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + filteredItems.length) % filteredItems.length,
    );
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen]);

  return (
    <>
      <section
        ref={sectionRef}
        id="plastering-gallery"
        className="bg-white py-24 lg:py-32"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="reveal mb-12 text-center lg:mb-16">
            <div className="text-luxury-green mb-6 inline-flex items-center space-x-3">
              <div className="bg-luxury-green h-0.5 w-12" />
              <span className="text-sm font-semibold tracking-[0.3em] uppercase">
                Реальные объекты
              </span>
              <div className="bg-luxury-green h-0.5 w-12" />
            </div>
            <h2 className="font-display text-luxury-dark mb-6 text-5xl font-bold lg:text-7xl">
              Фактура и стяжка
            </h2>
            <p className="text-luxury-stone mx-auto max-w-3xl text-xl">
              Портретные кадры со свежих объектов показывают точность геометрии,
              чистоту штукатурки и идеальную плоскость полусухой стяжки.
            </p>
          </div>

          <div className="reveal mb-16 flex flex-wrap items-center justify-center gap-3 lg:mb-20">
            <button
              onClick={() => setActiveFilter("all")}
              className={`plastering-filter-btn ${activeFilter === "all" ? "active" : ""}`}
              aria-pressed={activeFilter === "all"}
            >
              Все
            </button>
            <button
              onClick={() => setActiveFilter("walls")}
              className={`plastering-filter-btn ${activeFilter === "walls" ? "active" : ""}`}
              aria-pressed={activeFilter === "walls"}
            >
              Штукатурка стен
            </button>
            <button
              onClick={() => setActiveFilter("floors")}
              className={`plastering-filter-btn ${activeFilter === "floors" ? "active" : ""}`}
              aria-pressed={activeFilter === "floors"}
            >
              Стяжка пола
            </button>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8 xl:grid-cols-5">
            {filteredItems.map((item, index) => (
              <button
                key={index}
                onClick={() => openLightbox(index)}
                className="plastering-card reveal group w-full"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="plastering-card-media aspect-[3/4] overflow-hidden rounded-sm">
                  <ResponsiveImage
                    image={item}
                    className="h-full w-full object-cover"
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 20vw"
                  />
                  <div className="plastering-card-shine" />
                  <div className="plastering-card-icon">
                    <Maximize2 className="h-5 w-5" />
                  </div>
                </div>
                <div className="plastering-card-caption">
                  <p className="text-luxury-stone text-sm">{item.caption}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/80 px-4 py-10 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/80 transition-colors hover:text-white"
            aria-label="Закрыть просмотр"
          >
            <X className="h-8 w-8" />
          </button>

          <div
            className="w-full max-w-4xl space-y-6 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex items-center justify-center gap-3 sm:gap-6">
              <button
                onClick={prevImage}
                className="plastering-lightbox-nav"
                aria-label="Предыдущее фото"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>

              <ResponsiveImage
                image={filteredItems[currentIndex]}
                className="mx-auto max-h-[75vh] w-auto rounded-sm shadow-2xl"
                sizes="100vw"
              />

              <button
                onClick={nextImage}
                className="plastering-lightbox-nav"
                aria-label="Следующее фото"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-2">
              <p className="text-sm leading-relaxed text-white/85 sm:text-base">
                {filteredItems[currentIndex].caption}
              </p>
              <p className="text-xs tracking-[0.4em] text-white/50 uppercase">
                {currentIndex + 1} / {filteredItems.length}
              </p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .plastering-filter-btn {
          position: relative;
          padding: 0.75rem 1.75rem;
          border-radius: 9999px;
          border: 1px solid rgba(34, 197, 94, 0.35);
          background: rgba(255, 255, 255, 0.8);
          color: #8b8680;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          font-size: 0.75rem;
          transition: all 0.4s ease;
        }

        .plastering-filter-btn.active {
          color: #0a0a0a;
          border-color: rgba(22, 163, 74, 0.85);
          box-shadow: 0 15px 35px rgba(34, 197, 94, 0.2);
          background: linear-gradient(
            135deg,
            rgba(34, 197, 94, 0.15),
            rgba(134, 239, 172, 0.35)
          );
        }

        .plastering-card {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          transition:
            transform 0.6s cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 0.6s ease;
        }

        .plastering-card-media {
          position: relative;
          border-radius: 0.4rem;
          overflow: hidden;
          background: #000;
        }

        .plastering-card-shine {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            120deg,
            rgba(255, 255, 255, 0.25),
            rgba(255, 255, 255, 0)
          );
          mix-blend-mode: screen;
          opacity: 0;
          transition: opacity 0.35s ease;
        }

        .plastering-card-icon {
          position: absolute;
          bottom: 1.25rem;
          right: 1.25rem;
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 9999px;
          background: rgba(10, 10, 10, 0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #faf7f0;
          opacity: 0;
          transform: translateY(12px);
          transition: all 0.4s ease;
        }

        .plastering-card-caption {
          padding-left: 0.25rem;
          padding-right: 0.25rem;
          transform: translateY(6px);
          transition:
            transform 0.4s ease,
            opacity 0.4s ease;
          opacity: 0.85;
        }

        @media (hover: hover) {
          .plastering-card:hover {
            transform: translateY(-12px);
            box-shadow: 0 35px 65px rgba(10, 10, 10, 0.15);
          }

          .plastering-card:hover .plastering-card-shine {
            opacity: 1;
          }

          .plastering-card:hover .plastering-card-icon {
            opacity: 1;
            transform: translateY(0);
          }

          .plastering-card:hover .plastering-card-caption {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .plastering-lightbox-nav {
          width: 2.75rem;
          height: 2.75rem;
          border-radius: 9999px;
          border: 1px solid rgba(255, 255, 255, 0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255, 255, 255, 0.85);
          background: rgba(10, 10, 10, 0.35);
          transition: all 0.3s ease;
        }

        @media (hover: hover) {
          .plastering-lightbox-nav:hover {
            transform: translateY(-4px);
            border-color: rgba(134, 239, 172, 0.6);
            background: rgba(34, 197, 94, 0.3);
            color: #faf7f0;
          }
        }
      `}</style>
    </>
  );
}
